import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import type * as React from 'react';

import { useKeyboardNav } from '../../hooks/useKeyboardNav';
import { cn } from '../../styles/lib/utils';

function NavMenu({ ...props }: React.ComponentProps<typeof DropdownMenuPrimitive.Root>) {
  return <DropdownMenuPrimitive.Root data-slot="nav-menu" {...props} />;
}

function NavMenuTrigger({ className, asChild, ...props }: React.ComponentProps<typeof DropdownMenuPrimitive.Trigger>) {
  return (
    <DropdownMenuPrimitive.Trigger
      data-slot="nav-menu-trigger"
      asChild={asChild}
      className={
        asChild
          ? className
          : cn(
              'inline-flex h-7 items-center gap-1 rounded-lg border-0 bg-transparent px-2 select-none',
              'font-noto text-xs font-medium leading-none text-primary-dark transition-colors',
              'cursor-pointer hover:bg-orange-10',
              'focus-ring',
              'data-[state=open]:bg-orange-10',
              '[&_svg]:size-4 [&_svg]:shrink-0',
              className,
            )
      }
      {...props}
    />
  );
}

function NavMenuContent({
  className,
  align = 'start',
  sideOffset = 4,
  children,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Content>) {
  const keyboardNav = useKeyboardNav();
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        data-slot="nav-menu-content"
        // Radix focuses the item under the pointer, so items ring the highlight only while the keyboard is in use.
        data-keyboard-nav={keyboardNav || undefined}
        align={align}
        sideOffset={sideOffset}
        className={cn(
          'z-50 inline-flex flex-col items-stretch min-w-52 rounded-sm border border-grey-30 bg-white font-noto',
          'divide-y divide-grey-30 overflow-hidden shadow-md',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
          'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          'origin-(--radix-dropdown-menu-content-transform-origin)',
          className,
        )}
        {...props}
      >
        {children}
      </DropdownMenuPrimitive.Content>
    </DropdownMenuPrimitive.Portal>
  );
}

type NavMenuHeaderProps = React.ComponentProps<'div'> & {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
};

function NavMenuHeader({ className, title, subtitle, children, ...props }: NavMenuHeaderProps) {
  return (
    <div
      data-slot="nav-menu-header"
      role="presentation"
      className={cn('flex flex-col gap-1 px-3 py-2.5 text-grey-70', className)}
      {...props}
    >
      {children ?? (
        <>
          {title != null && <div className="text-sm font-semibold leading-tight">{title}</div>}
          {subtitle != null && <div className="text-xs leading-tight">{subtitle}</div>}
        </>
      )}
    </div>
  );
}

type NavMenuGroupProps = React.ComponentProps<typeof DropdownMenuPrimitive.Group> & {
  label?: React.ReactNode;
  active?: boolean;
};

function NavMenuGroup({ className, label, active, children, ...props }: NavMenuGroupProps) {
  return (
    <DropdownMenuPrimitive.Group
      data-slot="nav-menu-group"
      data-active={active || undefined}
      className={cn(
        'flex flex-col gap-1 py-2.5',
        // Sub-items inside a group are smaller, indented, and don't render the highlight bar
        '[&_[data-slot=nav-menu-item]]:px-0 [&_[data-slot=nav-menu-item]]:py-1',
        '[&_[data-slot=nav-menu-item]]:pl-5 [&_[data-slot=nav-menu-item]]:pr-3',
        '[&_[data-slot=nav-menu-item]]:text-xs [&_[data-slot=nav-menu-item]]:leading-5',
        '[&_[data-slot=nav-menu-item]]:before:hidden',
        className,
      )}
      {...props}
    >
      {label != null && (
        <div className="flex items-center gap-2 pr-3">
          <span
            aria-hidden
            className={cn(
              'h-7 w-1 shrink-0 rounded-r-sm transition-colors',
              active ? 'bg-orange-50' : 'bg-transparent',
            )}
          />
          {/* Fills the row so a label can end in a badge. */}
          <span
            className={cn('min-w-0 flex-1 text-sm font-medium leading-tight', active ? 'text-grey-90' : 'text-grey-70')}
          >
            {label}
          </span>
        </div>
      )}
      <div className="flex flex-col">{children}</div>
    </DropdownMenuPrimitive.Group>
  );
}

type NavMenuItemProps = React.ComponentProps<typeof DropdownMenuPrimitive.Item> & {
  active?: boolean;
};

function NavMenuItem({ className, active, ...props }: NavMenuItemProps) {
  return (
    <DropdownMenuPrimitive.Item
      data-slot="nav-menu-item"
      data-active={active || undefined}
      className={cn(
        'group/nav-menu-item relative flex w-full cursor-pointer select-none items-center gap-2',
        'bg-transparent text-left outline-none',
        'px-3 py-2.5 text-xs font-medium leading-tight',
        'text-grey-70 transition-colors',
        'hover:bg-orange-10 hover:text-grey-90',
        'data-[highlighted]:bg-orange-10 data-[highlighted]:text-grey-90',
        'in-data-[keyboard-nav=true]:data-highlighted:highlight-ring-inset',
        'data-[active=true]:text-grey-90',
        'data-disabled:pointer-events-none data-disabled:opacity-50',
        'before:absolute before:left-0 before:top-1/2 before:h-7 before:w-1 before:-translate-y-1/2',
        'before:rounded-r-sm before:bg-transparent before:transition-colors',
        'data-[active=true]:before:bg-orange-50',
        '[&_a]:flex [&_a]:w-full [&_a]:items-center [&_a]:gap-2',
        className,
      )}
      {...props}
    />
  );
}

export { NavMenu, NavMenuTrigger, NavMenuContent, NavMenuHeader, NavMenuGroup, NavMenuItem };
